import moment from 'moment';
import React, { useEffect, useState } from 'react';
import DatePicker from 'react-datepicker';
import { IoIosArrowDown, IoIosArrowUp } from 'react-icons/io';
import {
  Button,
  Col,
  Collapse,
  Container,
  Row,
  Table,
  Input
} from 'reactstrap';
import { GET_AUDIT_LOGS_IN_DATE_RANGE } from '../queries';
import { handleExportCSV } from '../utilities/auditLogs';
import Pagination from './Pagination';
import Select from 'react-select';
import { useQuery } from 'react-apollo';
import { targetTypeOptions, actionOptions, doneByOptions } from '../utilities';
import Loader from './Loader';

const auditsQuery = {
  startDate: null,
  endDate: null,
  searchText: '',
  targetId: ''
};

const AuditLogsTab = () => {
  const [auditQuery, setAuditQuery] = useState(auditsQuery);
  const [submittedQuery, setSubmittedQuery] = useState(auditsQuery);
  const [selectedAuditRow, setSelectedAuditRow] = useState(null);
  const [selectedTargetType, setSelectedTargetType] = useState('ALL');
  const [selectedAction, setSelectedAction] = useState('ALL');
  const [selectedDoneBy, setSelectedDoneBy] = useState('ALL');
  const [searchText, setSearchText] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [logs, setLogs] = useState([]);
  const [cursorId, setCursorId] = useState(null);
  const [hasSearched, setHasSearched] = useState(false);
  const [targetId, setTargetId] = useState('');

  const rowsPerPage = 10;
  const isSubmitDisabled =
    !auditQuery.startDate ||
    !auditQuery.endDate ||
    (searchText === submittedQuery.searchText &&
      targetId === submittedQuery.targetId &&
      moment(auditQuery.startDate).isSame(submittedQuery.startDate, 'day') &&
      moment(auditQuery.endDate).isSame(submittedQuery.endDate, 'day'));

  const handlePageChange = pageNumber => {
    setCurrentPage(pageNumber);
  };

  const { data, loading, fetchMore } = useQuery(GET_AUDIT_LOGS_IN_DATE_RANGE, {
    variables: { input: submittedQuery },
    fetchPolicy: 'network-only',
    skip: !hasSearched
  });

  useEffect(() => {
    if (data && data.auditLogs) {
      setLogs(data.auditLogs);
      if (data.auditLogs.length > 0) {
        setCursorId(data.auditLogs[data.auditLogs.length - 1].id);
      }
    }
  }, [data]);

  useEffect(() => {
    if (!hasSearched) return;
    const requiredRows = currentPage * rowsPerPage;

    if (requiredRows >= logs.length && !loading) {
      fetchMore({
        variables: {
          input: {
            ...submittedQuery,
            cursorId
          }
        },
        updateQuery: (prev, { fetchMoreResult }) => {
          if (!fetchMoreResult.auditLogs.length) {
            return prev;
          }
          const newLogs = fetchMoreResult.auditLogs;

          setCursorId(newLogs[newLogs.length - 1].id);
          setLogs(prevLogs => [...prevLogs, ...newLogs]);

          return {
            auditLogs: [...prev.auditLogs, ...newLogs]
          };
        }
      });
    }
    // eslint-disable-next-line
  }, [currentPage]);

  const handleAuditRowClick = id => {
    setSelectedAuditRow(prev => (prev === id ? null : id));
  };

  const isDateRangeValid = (startDate, endDate) => {
    const maxEndDate = moment(startDate).add(7, 'days');
    return moment(endDate).isSameOrBefore(maxEndDate, 'day');
  };

  const handleStartDateChange = date => {
    const startDate = moment(date)
      .startOf('day')
      .toDate();
    if (searchText || targetId) {
      setAuditQuery({ startDate, endDate: startDate });
      return;
    }
    const endDate = auditQuery.endDate ? moment(auditQuery.endDate) : null;
    if (!endDate) {
      setAuditQuery({ ...auditQuery, startDate });
      return;
    }

    let maxEndDate = moment(startDate)
      .add(7, 'days')
      .endOf('day')
      .toDate();

    // Check if the calculated maxEndDate is after the current date
    const currentDate = moment()
      .endOf('day')
      .toDate();
    if (moment(maxEndDate).isAfter(currentDate)) {
      maxEndDate = currentDate;
    }

    if (isDateRangeValid(startDate, endDate)) {
      if (moment(startDate).isAfter(moment(endDate))) {
        setAuditQuery({ startDate, endDate: maxEndDate });
      } else {
        setAuditQuery({ ...auditQuery, startDate });
      }
    } else {
      // Adjust end date if the range exceeds 7 days
      setAuditQuery({
        startDate,
        endDate: maxEndDate
      });
    }
  };

  const handleEndDateChange = date => {
    const endDate = moment(date)
      .endOf('day')
      .toDate();
    const startDate = auditQuery.startDate
      ? moment(auditQuery.startDate)
      : null;

    if (!startDate) {
      setAuditQuery({ ...auditQuery, endDate });
      return;
    }

    if (searchText || targetId) {
      setAuditQuery({ ...auditQuery, endDate: startDate });
      return;
    }

    if (isDateRangeValid(startDate, endDate)) {
      setAuditQuery({ ...auditQuery, endDate });
    } else {
      setAuditQuery({
        ...auditQuery,
        endDate: moment(startDate)
          .add(7, 'days')
          .endOf('day')
          .toDate()
      });
    }
  };

  useEffect(() => {
    setSelectedAuditRow(null);
  }, [
    searchText,
    selectedTargetType,
    selectedAction,
    selectedDoneBy,
    targetId
  ]);

  const handleSearchSubmit = () => {
    let startDate = moment(auditQuery.startDate)
      .startOf('day')
      .toDate();
    let endDate = moment(auditQuery.endDate)
      .endOf('day')
      .toDate();

    if (searchText || targetId) {
      // restrict to single day
      startDate = moment(auditQuery.startDate)
        .startOf('day')
        .toDate();
      endDate = moment(auditQuery.startDate)
        .endOf('day')
        .toDate();
    } else {
      // max 7 days
      const maxEndDate = moment(startDate)
        .add(7, 'days')
        .endOf('day')
        .toDate();
      if (moment(endDate).isAfter(maxEndDate)) {
        endDate = maxEndDate;
      }
    }

    const queryInput = {
      startDate,
      endDate,
      searchText: searchText || '',
      targetId: targetId ? String(targetId).trim() : ''
    };
    if (selectedTargetType !== 'ALL')
      queryInput.targetType = selectedTargetType;
    if (selectedAction !== 'ALL') queryInput.action = selectedAction;
    if (selectedDoneBy !== 'ALL') queryInput.userName = selectedDoneBy;

    setSubmittedQuery(queryInput);
    setCurrentPage(1);
    setCursorId(null);
    setHasSearched(true);
  };

  let filteredLogs = logs;
  if (selectedTargetType !== 'ALL') {
    filteredLogs = filteredLogs.filter(
      log => log.targetType === selectedTargetType
    );
  }
  if (selectedAction !== 'ALL') {
    filteredLogs = filteredLogs.filter(log => log.action === selectedAction);
  }
  if (selectedDoneBy !== 'ALL') {
    filteredLogs = filteredLogs.filter(log => log.userName === selectedDoneBy);
  }

  const totalRows = filteredLogs.length;
  const pageCount = Math.ceil(totalRows / rowsPerPage);
  const startIndex = (currentPage - 1) * rowsPerPage;
  const paginatedLogs = filteredLogs.slice(
    startIndex,
    startIndex + rowsPerPage
  );

  return (
    <Container fluid className="logs-container">
      <Row className="filters-row">
        <Col md={2}>
          <div className="filter-field">
            <label>From</label>
            <DatePicker
              className="audit-input"
              selected={
                auditQuery.startDate ? moment(auditQuery.startDate) : null
              }
              showIcon
              placeholderText="Select start date"
              maxDate={moment()}
              dateFormat="DD MMM YYYY"
              selectsStart
              startDate={
                auditQuery.startDate ? moment(auditQuery.startDate) : null
              }
              endDate={auditQuery.endDate ? moment(auditQuery.endDate) : null}
              showMonthDropdown
              showYearDropdown
              dropdownMode="select"
              onChange={handleStartDateChange}
            />
          </div>
        </Col>
        <Col md={2}>
          <div className="filter-field">
            <label>To</label>
            <DatePicker
              className="audit-input"
              selected={auditQuery.endDate ? moment(auditQuery.endDate) : null}
              placeholderText="Select end date"
              showIcon
              maxDate={moment()}
              selectsEnd
              startDate={
                auditQuery.startDate ? moment(auditQuery.startDate) : null
              }
              endDate={auditQuery.endDate ? moment(auditQuery.endDate) : null}
              minDate={
                auditQuery.startDate ? moment(auditQuery.startDate) : null
              }
              dateFormat="DD MMM YYYY"
              showMonthDropdown
              showYearDropdown
              dropdownMode="select"
              onChange={handleEndDateChange}
            />
          </div>
        </Col>
        <Col md={6}>
          <div className="filter-field">
            <label>Search</label>
            <div className="dual-input">
              <Input
                className="filter-control"
                type="text"
                value={searchText}
                onChange={e => {
                  const value = e.target.value;
                  if (/^[a-zA-Z0-9\s()]*$/.test(value)) {
                    setSearchText(value);
                    setAuditQuery({
                      ...auditQuery,
                      endDate: auditQuery.startDate
                    });
                  }
                }}
                placeholder="Search for Target / Done By"
              />
              <Input
                className="filter-control"
                type="text"
                value={targetId}
                onChange={e => {
                  const value = e.target.value;
                  if (/^[a-zA-Z0-9_-]*$/.test(value)) {
                    setTargetId(value);
                    setAuditQuery({
                      ...auditQuery,
                      endDate: auditQuery.startDate
                    });
                  }
                }}
                placeholder="Search by Target ID"
              />
            </div>
          </div>
        </Col>
        <Col md={2}>
          <div className="filter-field">
            <label>&nbsp;</label>
            <Button
              color="primary"
              className="primary-btn"
              onClick={handleSearchSubmit}
              disabled={isSubmitDisabled}
            >
              {loading ? 'Loading' : 'Submit'}
            </Button>
          </div>
        </Col>
      </Row>

      <Row className="filters-row">
        <Col md={3}>
          <div className="filter-field">
            <label>Target Type</label>
            <Select
              className="audit-select"
              value={targetTypeOptions.find(
                opt => opt.value === selectedTargetType
              )}
              onChange={opt => setSelectedTargetType(opt.value)}
              options={targetTypeOptions}
            />
          </div>
        </Col>
        <Col md={3}>
          <div className="filter-field">
            <label>Action</label>
            <Select
              className="audit-select"
              value={actionOptions.find(opt => opt.value === selectedAction)}
              onChange={opt => setSelectedAction(opt.value)}
              options={actionOptions}
            />
          </div>
        </Col>
        <Col md={3}>
          <div className="filter-field">
            <label>Done By</label>
            <Select
              className="audit-select"
              value={doneByOptions.find(opt => opt.value === selectedDoneBy)}
              onChange={opt => setSelectedDoneBy(opt.value)}
              options={doneByOptions}
            />
          </div>
        </Col>

        <Col md={3}>
          <div className="filter-field">
            <label>&nbsp;</label>
            <Button
              color="warning"
              className="export-btn font-weight-bold"
              onClick={() => handleExportCSV(filteredLogs)}
              disabled={!hasSearched || filteredLogs.length === 0}
            >
              Export as CSV
            </Button>
          </div>
        </Col>
      </Row>
      <Row>
        {loading ? (
          <Loader size={10} />
        ) : !hasSearched ? (
          <div className="no-logs-text">Please apply filters to search!</div>
        ) : filteredLogs.length === 0 ? (
          <div className="no-logs-text">{"That's all, folks!"}</div>
        ) : (
          <>
            <div className="audit-logs-table">
              <Table striped responsive>
                <thead>
                  <tr>
                    <th></th>
                    <th>TIMESTAMP</th>
                    <th>TARGET</th>
                    <th>TARGET TYPE</th>
                    <th>ACTION</th>
                    <th>DONE BY</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedLogs.map(log => {
                    const {
                      created_at,
                      targetName,
                      targetType,
                      action,
                      userName,
                      meta,
                      id
                    } = log;
                    return (
                      <>
                        <tr
                          key={`${id}-title`}
                          onClick={() => handleAuditRowClick(id)}
                        >
                          <td>
                            {selectedAuditRow === id ? (
                              <IoIosArrowUp />
                            ) : (
                              <IoIosArrowDown />
                            )}{' '}
                          </td>
                          <td>
                            {moment(created_at).format('DD MMM YYYY HH:mm')}
                          </td>
                          <td>{targetName}</td>
                          <td>{targetType}</td>
                          <td>{action.toUpperCase()}</td>
                          <td>{userName}</td>
                        </tr>
                        {selectedAuditRow === id && (
                          <tr key={`${id}-meta`}>
                            <td colSpan={7}>
                              <Collapse isOpen={selectedAuditRow === id}>
                                <pre
                                  style={{
                                    overflow: 'auto',
                                    wordWrap: 'normal',
                                    width: '1100px'
                                  }}
                                >
                                  {JSON.stringify(meta, null, 2)}
                                </pre>
                              </Collapse>
                            </td>
                          </tr>
                        )}
                      </>
                    );
                  })}
                </tbody>
              </Table>
            </div>
            <div className="pagination-container">
              <Pagination
                currentPage={currentPage}
                setCurrentPage={handlePageChange}
                totalPages={pageCount}
              />
            </div>
          </>
        )}
      </Row>
    </Container>
  );
};
export default AuditLogsTab;
